import React, { PureComponent } from 'react';
import { SidePanelContext } from '../../SidePanelAPI';
import PropTypes from 'prop-types';
import { st, classes } from './Header.st.css';
import { dataHooks } from '../../constants';
import Heading from '../../../Heading';
import Divider from '../../../Divider';
import CloseButton from '../../../CloseButton';
import Box from '../../../Box';
import InfoIcon from '../../../InfoIcon';
import Text from '../../../Text';
import { TooltipCommonProps } from '../../../common/PropTypes/TooltipCommon';
import { HeaderProps } from './Header.types';
import { SidePanelProps } from '../../SidePanel.types';
import { Help24 as Help } from '@wix/wix-ui-icons-common/system';
import { ChevronLeftLarge } from '@wix/wix-ui-icons-common';
import Tooltip from '../../../Tooltip';

class Header extends PureComponent<HeaderProps> {
  static displayName = 'SidePanel.Header';

  static propTypes = {
    className: PropTypes.string,
    title: PropTypes.node,
    subtitle: PropTypes.node,
    infoTooltipContent: PropTypes.node,
    infoTooltipProps: PropTypes.shape(TooltipCommonProps),
    showDivider: PropTypes.bool,
    children: PropTypes.node,
    maxLines: PropTypes.number,
    ellipsis: PropTypes.bool,
  };

  static defaultProps = {
    showDivider: true,
  };

  renderStringTitle(skin: SidePanelProps['skin']) {
    const { title, infoTooltipContent, infoTooltipProps, maxLines, ellipsis } =
      this.props;

    const headerProps = {
      placement: 'auto',
      ellipsis,
      maxLines,
      size: 'medium',
      as: 'h2',
      dataHook: dataHooks.sidePanelHeaderTitle,
    } as const;

    const HeaderText = () =>
      skin === 'floating' ? (
        <Text className={classes.floatingTitle} {...headerProps} weight="bold">
          {title}
        </Text>
      ) : (
        <Heading {...headerProps}>{title}</Heading>
      );

    return (
      <Box>
        <HeaderText />
        {infoTooltipContent && (
          <Box marginLeft="tiny">
            <InfoIcon
              dataHook={dataHooks.sidePanelHeaderTitleInfoIcon}
              content={infoTooltipContent}
              tooltipProps={infoTooltipProps}
            />
          </Box>
        )}
      </Box>
    );
  }

  renderSubtitle() {
    const { subtitle } = this.props;
    if (!subtitle) return null;

    const isStringSubtitle = typeof subtitle === 'string';

    if (!isStringSubtitle) {
      return subtitle;
    }

    return (
      <Text
        skin="standard"
        secondary
        dataHook={dataHooks.sidePanelHeaderSubtitle}
      >
        {subtitle}
      </Text>
    );
  }

  renderBackButton({
    ariaLabel,
    onClick,
    description,
    tooltipProps,
  }: SidePanelProps['backButtonProps'] = {}) {
    const backButton = (
      <CloseButton
        dataHook={dataHooks.sidePanelHeaderBackButton}
        className={classes.backButton}
        onClick={onClick}
        size="large"
        skin="dark"
        aria-label={ariaLabel}
        children={<ChevronLeftLarge />}
      />
    );

    if (description) {
      return (
        <Tooltip
          {...tooltipProps}
          content={description}
          dataHook={dataHooks.sidePanelHeaderBackButtonTooltip}
        >
          {backButton}
        </Tooltip>
      );
    }

    return backButton;
  }

  renderTitle(
    skin?: SidePanelProps['skin'],
    backButtonProps?: SidePanelProps['backButtonProps'],
    closeButtonProps?: SidePanelProps['closeButtonProps'],
    helpButtonProps?: SidePanelProps['helpButtonProps'],
  ) {
    const { title } = this.props;
    const isStringTitle = typeof title === 'string';

    return (
      <div
        className={st(classes.titleContainer, {
          noCloseButton: !closeButtonProps?.onClick,
        })}
      >
        {this.props.prefix && (
          <div data-hook={dataHooks.sidePanelHeaderPrefix}>
            {this.props.prefix}
          </div>
        )}

        {backButtonProps?.onClick && this.renderBackButton(backButtonProps)}

        <div className={classes.title}>
          {isStringTitle ? this.renderStringTitle(skin) : title}
          {this.renderSubtitle()}
        </div>

        {this.renderButtons(closeButtonProps, helpButtonProps)}
      </div>
    );
  }

  renderButtons(
    closeButtonProps?: SidePanelProps['closeButtonProps'],
    helpButtonProps?: SidePanelProps['helpButtonProps'],
  ) {
    if (
      !closeButtonProps?.onClick &&
      !helpButtonProps?.onClick &&
      !this.props.suffix
    ) {
      return <></>;
    }

    return (
      <div className={classes.buttonBox}>
        {this.props.suffix && (
          <div data-hook={dataHooks.sidePanelHeaderSuffix}>
            {this.props.suffix}
          </div>
        )}

        {helpButtonProps?.onClick && (
          <CloseButton
            dataHook={dataHooks.sidePanelHeaderHelpButton}
            className={classes.helpButton}
            onClick={helpButtonProps.onClick}
            size={helpButtonProps.size ?? 'large'}
            skin={helpButtonProps.skin ?? 'dark'}
            aria-label={helpButtonProps.ariaLabel}
            children={<Help className={classes.helpIcon} />}
          />
        )}
        {closeButtonProps?.onClick && (
          <CloseButton
            dataHook={dataHooks.sidePanelHeaderCloseButton}
            className={classes.closeButton}
            onClick={closeButtonProps.onClick}
            size={closeButtonProps.size ?? 'large'}
            aria-label={closeButtonProps.ariaLabel}
            skin={closeButtonProps.skin ?? 'dark'}
          />
        )}
      </div>
    );
  }

  render() {
    const { children, showDivider, className } = this.props;
    return (
      <SidePanelContext.Consumer>
        {({
          skin,
          isDraggable,
          backButtonProps,
          closeButtonProps,
          helpButtonProps,
        }) => (
          <div
            className={st(classes.root, { skin, isDraggable }, className)}
            data-hook={dataHooks.sidePanelHeader}
          >
            {this.renderTitle(
              skin,
              backButtonProps,
              closeButtonProps,
              helpButtonProps,
            )}
            {children}
            {showDivider && (
              <Divider
                className={classes.divider}
                dataHook={dataHooks.sidePanelHeaderDivider}
              />
            )}
          </div>
        )}
      </SidePanelContext.Consumer>
    );
  }
}

export default Header;
